'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

import { SlideOver } from '@/components/admin/SlideOver';
import { slugFromName } from '@/components/platform/format';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseCatalog, parsePlan, type CatalogPlan } from '@/lib/platform/catalog';
import { FEATURE_CATALOG, FEATURE_LABELS, groupFeatures } from '@/lib/entitlements';
import type { PlatformTenantRow } from '@/lib/platform/summary';
import { usePlatformSummary } from '@/pages/platform/usePlatformSummary';

const fieldClass = 'mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand';

export function PlansPage() {
  const { overview } = usePlatformSummary('Could not load agency counts.');
  const [plans, setPlans] = useState<CatalogPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CatalogPlan | null>(null);
  const [creating, setCreating] = useState(false);
  const [listing, setListing] = useState<CatalogPlan | null>(null);
  const [pausing, setPausing] = useState<CatalogPlan | null>(null);
  const tenants = overview?.tenants ?? [];

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/platform/plans')
      .then((payload) => {
        if (!cancelled) {
          setPlans(parseCatalog(payload));
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && !leaveIfSignedOut(err)) setError(accountError(err, 'Could not load the plan catalog.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function replace(plan: CatalogPlan) {
    setPlans((current) => {
      if (!current?.some((row) => row.id === plan.id)) return [...(current ?? []), plan];
      return current.map((row) => (row.id === plan.id ? { ...plan, assignedAgencies: row.assignedAgencies } : row));
    });
  }

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="type-section text-ink">Plans</h2>
          <p className="text-sm text-muted">Configure SaaS subscription tiers, vehicle quotas, and feature entitlements in MAD.</p>
        </div>
        <button type="button" onClick={() => setCreating(true)} className="shrink-0 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">
          Add plan
        </button>
      </div>
      {plans && <Metrics plans={plans} tenants={tenants} />}
      {error && <p className="rounded-2xl border border-danger/40 bg-card px-4 py-6 text-sm text-danger">{error}</p>}
      {plans === null && !error && <div className="h-40 animate-pulse rounded-2xl bg-card" aria-hidden="true" />}
      {plans && (
        <div className="grid gap-4 lg:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} onEdit={() => setEditing(plan)} onList={() => setListing(plan)} onToggle={() => setPausing(plan)} />
          ))}
        </div>
      )}
      {editing && (
        <PlanEditor
          plan={editing}
          mode="edit"
          onClose={() => setEditing(null)}
          onSaved={(plan) => {
            replace(plan);
            setEditing(null);
          }}
        />
      )}
      {creating && (
        <PlanEditor
          plan={blankPlan()}
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={(plan) => {
            replace(plan);
            setCreating(false);
          }}
        />
      )}
      {listing && <AssignedAgencies plan={listing} tenants={tenants.filter((row) => row.subscriptionTier === listing.id)} onClose={() => setListing(null)} />}
      <ConfirmDialog
        open={pausing !== null}
        title={pausing?.active ? `Stop assigning ${pausing.name}?` : `Allow ${pausing?.name ?? 'this plan'} again?`}
        description={pausing?.active
          ? 'New agencies cannot copy this plan. Agencies already on it keep their caps.'
          : 'New agencies can copy this plan again. Saving still does not charge a card.'}
        confirmLabel={pausing?.active ? 'Deactivate' : 'Activate'}
        pending={false}
        onCancel={() => setPausing(null)}
        onConfirm={() => {
          if (!pausing) return;
          void saveActive(pausing, !pausing.active, replace).finally(() => setPausing(null));
        }}
      />
    </section>
  );
}

function Metrics({ plans, tenants }: { plans: CatalogPlan[]; tenants: PlatformTenantRow[] }) {
  const popular = mostPopular(plans, tenants);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Card label="Total plans" value={String(plans.length)} />
      <Card label="Subscribed agencies" value={String(tenants.length)} />
      <Card label="Most popular tier" value={popular} />
      <Card label="Platform fleet capacity" value={fleetCapacity(tenants)} />
    </div>
  );
}

function PlanCard({
  plan,
  onEdit,
  onList,
  onToggle,
}: {
  plan: CatalogPlan;
  onEdit: () => void;
  onList: () => void;
  onToggle: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const saved = savingsPercent(plan.monthlyPrice, plan.annualPrice);
  return (
    <article className="flex flex-col rounded-2xl border border-line bg-card p-4">
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-ink">{plan.name}</h3>
          <p className="font-mono text-xs text-muted">{plan.id}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass(plan)}`}>{badgeLabel(plan)}</span>
      </header>
      <p className="mt-3 text-sm text-muted">{plan.description}</p>
      <p className="mt-4 text-lg font-semibold text-ink">{dh(plan.monthlyPrice)} <span className="text-xs font-medium text-muted">/ mo</span></p>
      <p className="text-sm text-ink">
        {dh(plan.annualPrice)} <span className="text-xs text-muted">/ yr</span>
        {saved !== null && <span className="ms-2 rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success">Save {saved}%</span>}
      </p>
      <p className="mt-1 text-xs text-muted">{plan.trialDays} day trial</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Quota label="Vehicles" value={cap(plan.vehicleLimit)} />
        <Quota label="Staff" value={cap(plan.staffLimit)} />
      </div>
      <div className="mt-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Module access</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {plan.entitlements.slice(0, 4).map((key) => (
            <span key={key} className="rounded-full bg-shell px-2 py-0.5 text-[10px] font-semibold text-ink">
              {FEATURE_LABELS[key] ?? key}
            </span>
          ))}
          {plan.entitlements.length === 0 && <span className="text-[10px] text-muted">No modules</span>}
          {plan.entitlements.length > 4 && (
            <span className="rounded-full border border-line px-2 py-0.5 text-[10px] text-muted">
              +{plan.entitlements.length - 4} more
            </span>
          )}
        </div>
      </div>
      <ul className="mt-4 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Highlights</p>
        {plan.features.map((feature) => <Feature key={feature} on label={feature} />)}
      </ul>
      <footer className="mt-auto flex items-center justify-between gap-2 pt-4">
        <button type="button" onClick={onList} className="text-xs font-semibold text-ink underline">
          {plan.assignedAgencies} {plan.assignedAgencies === 1 ? 'agency' : 'agencies'} assigned
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onEdit} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">Edit plan</button>
          <div className="relative">
            <button type="button" aria-label="More actions" onClick={() => setMenu((open) => !open)} className="rounded-full border border-line px-2 py-1 text-xs text-ink">···</button>
            {menu && (
              <div className="absolute end-0 z-10 mt-1 w-40 rounded-xl border border-line bg-card p-1 shadow-lg">
                <button type="button" onClick={() => { setMenu(false); onToggle(); }} className="block w-full rounded-lg px-3 py-2 text-left text-xs text-ink hover:bg-shell">
                  {plan.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        </div>
      </footer>
    </article>
  );
}

function PlanEditor({ plan, mode, onClose, onSaved }: { plan: CatalogPlan; mode: 'create' | 'edit'; onClose: () => void; onSaved: (plan: CatalogPlan) => void }) {
  const [draft, setDraft] = useState(plan);
  const [codeEdited, setCodeEdited] = useState(mode === 'edit');
  const [features, setFeatures] = useState(plan.features.join('\n'));
  const [entitlements, setEntitlements] = useState<string[]>(plan.entitlements);
  const [unlimitedVehicles, setUnlimitedVehicles] = useState(plan.vehicleLimit === null);
  const [unlimitedStaff, setUnlimitedStaff] = useState(plan.staffLimit === null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saved = savingsPercent(draft.monthlyPrice, draft.annualPrice);

  function toggleEntitlement(key: string) {
    setEntitlements((current) => (
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    ));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (mode === 'create' && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(draft.id)) {
      setError('Code must be lowercase letters, numbers, and hyphens.');
      return;
    }
    setPending(true);
    setError(null);
    const body = {
      ...draft,
      vehicleLimit: unlimitedVehicles ? null : draft.vehicleLimit,
      staffLimit: unlimitedStaff ? null : draft.staffLimit,
      features: features.split('\n').map((line) => line.trim()).filter(Boolean),
      entitlements,
      customDomain: entitlements.includes('custom_domain'),
    };
    try {
      const savedPlan = parsePlan(await api<unknown>(mode === 'create' ? '/api/platform/plans' : `/api/platform/plans/${plan.id}`, {
        method: mode === 'create' ? 'POST' : 'PATCH',
        body: JSON.stringify(body),
      }));
      notify(mode === 'create' ? `${savedPlan.name} added.` : `${savedPlan.name} saved.`);
      onSaved(savedPlan);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not save this plan.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver
      open
      title={mode === 'create' ? 'Add plan' : `Edit ${plan.name}`}
      onClose={onClose}
      panelClassName="max-w-xl"
      footer={<button type="submit" form="plan-editor" disabled={pending} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">{pending ? 'Saving…' : mode === 'create' ? 'Add plan' : 'Save plan'}</button>}
    >
      <form id="plan-editor" onSubmit={(event) => void save(event)} className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">General</h3>
          <label className="block text-xs font-medium text-muted">Name<input value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value, id: mode === 'create' && !codeEdited ? slugFromName(event.target.value).slice(0, 40) : draft.id })} className={fieldClass} /></label>
          <label className="block text-xs font-medium text-muted">Code<input value={draft.id} readOnly={mode === 'edit'} maxLength={40} onChange={(event) => { setCodeEdited(true); setDraft({ ...draft, id: event.target.value.toLowerCase() }); }} className={`${fieldClass} ${mode === 'edit' ? 'text-muted' : ''}`} /></label>
          <label className="block text-xs font-medium text-muted">Description<input value={draft.description} maxLength={200} onChange={(event) => setDraft({ ...draft, description: event.target.value })} className={fieldClass} /></label>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Pricing and terms</h3>
          <p className="text-xs text-muted">Prices are labels in dirham. Saving does not charge a card.</p>
          <label className="block text-xs font-medium text-muted">Monthly DH<input value={draft.monthlyPrice} inputMode="decimal" onChange={(event) => setDraft({ ...draft, monthlyPrice: event.target.value })} className={fieldClass} /></label>
          <label className="block text-xs font-medium text-muted">
            Annual DH
            <input value={draft.annualPrice} inputMode="decimal" onChange={(event) => setDraft({ ...draft, annualPrice: event.target.value })} className={fieldClass} />
          </label>
          <button type="button" onClick={() => setDraft({ ...draft, annualPrice: suggestAnnual(draft.monthlyPrice) })} className="text-xs font-semibold text-ink underline">
            Suggest annual at two months free
          </button>
          {saved !== null && <p className="text-xs text-success">That annual price saves {saved}% against twelve monthly payments.</p>}
          <label className="block text-xs font-medium text-muted">Trial days<input type="number" min={0} max={90} value={draft.trialDays} onChange={(event) => setDraft({ ...draft, trialDays: Number(event.target.value) })} className={fieldClass} /></label>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Quotas</h3>
          <CapField label="Vehicle cap" unlimited={unlimitedVehicles} onUnlimited={setUnlimitedVehicles} value={draft.vehicleLimit} onChange={(value) => setDraft({ ...draft, vehicleLimit: value })} />
          <CapField label="Staff cap" unlimited={unlimitedStaff} onUnlimited={setUnlimitedStaff} value={draft.staffLimit} onChange={(value) => setDraft({ ...draft, staffLimit: value })} />
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Module access</h3>
          <p className="text-xs leading-relaxed text-muted">
            Choose what every agency on this plan can open. A platform admin can still grant an extra module to one
            agency without changing its plan.
          </p>
          <div className="space-y-3">
            {groupFeatures(FEATURE_CATALOG).map(({ group, features: grouped }) => (
              <div key={group}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{group}</p>
                <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                  {grouped.map((feature) => (
                    <label key={feature.key} className="flex cursor-pointer items-start gap-2 rounded-xl border border-line bg-shell px-2.5 py-2 text-xs">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-brand"
                        checked={entitlements.includes(feature.key)}
                        onChange={() => toggleEntitlement(feature.key)}
                      />
                      <span>
                        <span className="font-semibold text-ink">{feature.label}</span>
                        <span className="mt-0.5 block text-[10px] leading-snug text-muted">{feature.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Marketing highlights</h3>
          <label className="block text-xs font-medium text-muted">
            Shown on the plan card, one per line
            <textarea value={features} onChange={(event) => setFeatures(event.target.value)} rows={4} className={fieldClass} />
          </label>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Display</h3>
          <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={draft.featured} onChange={(event) => setDraft({ ...draft, featured: event.target.checked })} /> Featured on this page</label>
          <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Plan can be assigned to a new agency</label>
        </section>
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}

function AssignedAgencies({ plan, tenants, onClose }: { plan: CatalogPlan; tenants: PlatformTenantRow[]; onClose: () => void }) {
  return (
    <SlideOver open title={`${plan.name} agencies`} onClose={onClose}>
      {tenants.length === 0 && <p className="text-sm text-muted">No agency is on this plan.</p>}
      <ul className="divide-y divide-line">
        {tenants.map((tenant) => (
          <li key={tenant.slug} className="py-3">
            <Link to={`/super-admin/agencies/${tenant.slug}`} className="text-sm font-medium text-ink hover:underline">{tenant.name}</Link>
            <p className="text-xs text-muted">{tenant.city} · {tenant.status}</p>
          </li>
        ))}
      </ul>
    </SlideOver>
  );
}

function CapField({
  label,
  unlimited,
  onUnlimited,
  value,
  onChange,
}: {
  label: string;
  unlimited: boolean;
  onUnlimited: (value: boolean) => void;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted">
        {label}
        <input
          type="number"
          min={0}
          disabled={unlimited}
          value={unlimited ? '' : value ?? ''}
          placeholder="Unlimited"
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
          className={fieldClass}
        />
      </label>
      <label className="mt-2 flex items-center gap-2 text-xs text-ink">
        <input type="checkbox" checked={unlimited} onChange={(event) => onUnlimited(event.target.checked)} />
        Unlimited
      </label>
    </div>
  );
}

async function saveActive(plan: CatalogPlan, active: boolean, onSaved: (plan: CatalogPlan) => void) {
  try {
    const saved = parsePlan(await api<unknown>(`/api/platform/plans/${plan.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...plan, active }),
    }));
    onSaved({ ...saved, assignedAgencies: plan.assignedAgencies });
    notify(active ? `${plan.name} can be assigned.` : `${plan.name} cannot be assigned.`);
  } catch (err: unknown) {
    if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not update this plan.'), 'error');
  }
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-line bg-card px-4 py-3">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className="mt-1 text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

function Quota({ label, value }: { label: string; value: string }) {
  return <span className="rounded-full border border-line px-2 py-1 text-xs text-ink">{label}: {value}</span>;
}

function Feature({ on, label }: { on: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-2 text-sm ${on ? 'text-ink' : 'text-muted'}`}>
      <Check size={14} className={on ? 'text-success' : 'text-muted'} aria-hidden="true" />
      {label}
    </li>
  );
}

function blankPlan(): CatalogPlan {
  return {
    id: '',
    name: '',
    description: '',
    monthlyPrice: '0.00',
    annualPrice: '0.00',
    trialDays: 14,
    vehicleLimit: 15,
    staffLimit: 3,
    customDomain: false,
    features: [],
    entitlements: ['public_site', 'contracts'],
    active: true,
    featured: false,
    assignedAgencies: 0,
  };
}

function dh(value: string): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} DH`;
}

function cap(value: number | null): string {
  return value === null ? 'Unlimited' : String(value);
}

function savingsPercent(monthly: string, annual: string): number | null {
  const month = Number(monthly);
  const year = Number(annual);
  if (!Number.isFinite(month) || month <= 0 || !Number.isFinite(year)) return null;
  const full = month * 12;
  if (year >= full) return null;
  return Math.round((1 - year / full) * 100);
}

function suggestAnnual(monthly: string): string {
  const month = Number(monthly);
  if (!Number.isFinite(month)) return monthly;
  return (month * 10).toFixed(2);
}

function mostPopular(plans: CatalogPlan[], tenants: PlatformTenantRow[]): string {
  if (tenants.length === 0) return '—';
  let winner = plans[0];
  let best = -1;
  for (const plan of plans) {
    const count = tenants.filter((row) => row.subscriptionTier === plan.id).length;
    if (count > best) {
      best = count;
      winner = plan;
    }
  }
  return winner ? winner.name : '—';
}

function fleetCapacity(tenants: PlatformTenantRow[]): string {
  if (tenants.length === 0) return '—';
  if (tenants.some((row) => row.vehicleLimit === null)) return 'Unlimited';
  return String(tenants.reduce((sum, row) => sum + (row.vehicleLimit ?? 0), 0));
}

function badgeLabel(plan: CatalogPlan): string {
  if (!plan.active) return 'Legacy';
  if (plan.featured) return 'Featured';
  return 'Active';
}

function badgeClass(plan: CatalogPlan): string {
  if (!plan.active) return 'bg-warning/15 text-warning';
  if (plan.featured) return 'bg-brand/20 text-ink';
  return 'bg-success/15 text-success';
}
